import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, SignOut, MapPin, User, CaretDown, Warning, Car, ListChecks,
  ChartBar, Users as UsersIcon, WarningOctagon, Export, GearSix, ClipboardText, FileXls, Plugs, IdentificationCard, Key
} from "@phosphor-icons/react";
import { ValidationForm } from "@/components/ValidationForm";
import { ValidationResult } from "@/components/ValidationResult";
import { LiveRecordsTable } from "@/components/LiveRecordsTable";
import { TopRoomsToday } from "@/components/TopRoomsToday";
import { useRecentValidations, useSettings, useRiskLabels, useCustomRiskLevels } from "@/hooks/useValidations";
// Permitted tab components
import { AnalyticsTab } from "@/pages/admin/AnalyticsTab";
import { UsersTab } from "@/pages/admin/UsersTab";
import { ValidationsTab } from "@/pages/admin/ValidationsTab";
import { FlaggedTab } from "@/pages/admin/FlaggedTab";
import { ExportTab } from "@/pages/admin/ExportTab";
import { SettingsTab } from "@/pages/admin/SettingsTab";
import { AuditLogTab } from "@/pages/admin/AuditLogTab";

import { GuestRegistryTab } from "@/pages/admin/GuestRegistryTab";
import { InterfaceSetupTab } from "@/pages/admin/InterfaceSetupTab";
import { ProfileTab } from "@/pages/admin/ProfileTab";
import { SecurityTab } from "@/pages/admin/SecurityTab";
import { SecurityAdminTab } from "@/pages/admin/SecurityAdminTab";
import { KeyManagementTab } from "@/pages/admin/KeyManagementTab";
import { LastSyncBadge } from "@/components/LastSyncBadge";

const TAB_CONFIG = [
  { key: "validations", label: "Validations", icon: ListChecks, perm: "validations" },
  { key: "analytics", label: "Analytics", icon: ChartBar, perm: "analytics" },
  { key: "users", label: "Users", icon: UsersIcon, perm: "users" },
  { key: "flagged", label: "Flagged", icon: WarningOctagon, perm: "flagged" },
  { key: "export", label: "Export", icon: Export, perm: "export" },
  { key: "settings", label: "Settings", icon: GearSix, perm: "settings" },
  { key: "audit", label: "Audit Log", icon: ClipboardText, perm: "audit" },
  { key: "guest-registry", label: "Guest Registry", icon: FileXls, perm: "guest_registry" },
  { key: "interface", label: "Interface", icon: Plugs, perm: "interface" },
  { key: "security", label: "Security", icon: ShieldCheck, perm: "security" },
  { key: "security-admin", label: "Security Admin", icon: ShieldCheck, perm: "security_admin" },
  { key: "key-management", label: "Key Management", icon: Key, perm: "key_management" },
];

const TAB_COMPONENTS = {
  validations: ValidationsTab,
  analytics: AnalyticsTab,
  users: UsersTab,
  flagged: FlaggedTab,
  export: ExportTab,
  settings: SettingsTab,
  audit: AuditLogTab,
  "guest-registry": GuestRegistryTab,
  "interface": InterfaceSetupTab,
  "security": SecurityTab,
  "security-admin": SecurityAdminTab,
  "key-management": KeyManagementTab,
  profile: ProfileTab,
};

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [pendingValidation, setPendingValidation] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const perms = user?.permissions || [];
    const isSuper = user?.role === "superadmin";
    const canValidateInit = isSuper || perms.includes("car_validation") || perms.length === 0;
    // Use per-user default_page if set AND the user has access to that tab.
    const dp = user?.default_page;
    if (!dp || dp === "validate") return canValidateInit ? "validate" : "profile";
    if (dp === "profile") return "profile";
    const effective = perms.includes("change_user_password") && !perms.includes("users") ? [...perms, "users"] : perms;
    const permMap = { validations: "validations", analytics: "analytics", users: "users", flagged: "flagged", export: "export", settings: "settings", audit: "audit", "guest-registry": "guest_registry", interface: "interface", security: "security", "security-admin": "security_admin", "key-management": "key_management" };
    const requiredPerm = permMap[dp];
    if (isSuper || (requiredPerm && effective.includes(requiredPerm))) return dp;
    return canValidateInit ? "validate" : "profile";
  });
  const settings = useSettings();
  const timezone = settings?.timezone || "UTC";
  const riskLabels = useRiskLabels(settings);
  const customLevels = useCustomRiskLevels(settings);
  const { validations, reload, newIds } = useRecentValidations();

  const perms = user?.permissions || [];
  // 'change_user_password' implies access to the Users tab (so operators can perform password resets)
  const effectivePerms = perms.includes("change_user_password") && !perms.includes("users")
    ? [...perms, "users"]
    : perms;
  const isSuper = user?.role === "superadmin";
  const permittedTabs = isSuper ? TAB_CONFIG : TAB_CONFIG.filter((t) => effectivePerms.includes(t.perm));
  // Car Validation access: superadmin, users with explicit 'car_validation' perm, or legacy users with no permissions set at all
  const canValidate = user?.role === "superadmin" || perms.includes("car_validation") || perms.length === 0;

  const handleValidate = useCallback(async ({ room_number, car_plate }) => {
    setSubmitting(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/validations/preview", {
        room_number, car_plate,
        location: user?.location || "Unknown",
      });
      if (data.blocked) {
        setLastResult({ type: "blocked", message: data.reason });
        toast.error(data.reason, { duration: 5000 });
        setSubmitting(false);
        return;
      }
      // Store pending validation and show confirmation dialog
      setPendingValidation({ room_number, car_plate, location: user?.location || "Unknown" });
      setConfirmDialog(data);
      setSubmitting(false);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      toast.error(msg);
      setLastResult({ type: "error", message: msg });
      setSubmitting(false);
    }
  }, [user?.location]);

  const handleProceed = useCallback(async () => {
    if (!pendingValidation) return;
    setSubmitting(true);
    setConfirmDialog(null);
    try {
      const { data } = await api.post("/validations", pendingValidation);
      if (data.blocked) {
        setLastResult({ type: "blocked", message: data.reason });
        toast.error(data.reason, { duration: 5000 });
      } else {
        const v = data.validation;
        setLastResult({ type: "success", validation: v });
        if (v.risk_status === "fraud") {
          toast.error(`FRAUD ALERT: ${v.car_plate} — Score ${v.risk_score}`, { duration: 8000 });
        } else if (v.risk_status === "suspicious") {
          toast.warning(`Suspicious: ${v.car_plate} — Score ${v.risk_score}`, { duration: 6000 });
        } else {
          toast.success(`Validated: ${v.car_plate} for Room ${v.room_number}`, { duration: 3000 });
        }
        reload();
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      toast.error(msg);
      setLastResult({ type: "error", message: msg });
    } finally {
      setSubmitting(false);
      setPendingValidation(null);
    }
  }, [pendingValidation, reload]);

  const handleCancelValidation = useCallback(() => {
    setConfirmDialog(null);
    setPendingValidation(null);
    setSubmitting(false);
  }, []);

  const ActiveComponent = activeTab !== "validate" ? TAB_COMPONENTS[activeTab] : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={24} weight="bold" className="text-[#007AFF]" />
            <h1 className="font-heading text-lg font-bold text-foreground tracking-tight" data-testid="staff-header-title">
              {activeTab === "security" || activeTab === "security-admin" ? "GROUP WORKERS VALIDATION" : "CAR VALIDATION"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <LastSyncBadge />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin size={14} />
              <span className="font-mono-data text-xs">{user?.location || "\u2014"}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="user-menu-btn">
                  <User size={16} />
                  <span className="text-sm">{user?.name}</span>
                  <CaretDown size={12} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border min-w-[180px]">
                {/* Validate tab (permission-gated: 'car_validation') */}
                {canValidate && (
                  <DropdownMenuItem
                    onClick={() => setActiveTab("validate")}
                    className={`cursor-pointer ${activeTab === "validate" ? "text-[#007AFF]" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="nav-validate"
                  >
                    <Car size={14} className="mr-2" /> Validate Car
                  </DropdownMenuItem>
                )}
                {/* Permitted tabs */}
                {permittedTabs.map((tab) => (
                  <DropdownMenuItem
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`cursor-pointer ${activeTab === tab.key ? "text-[#007AFF]" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`nav-${tab.key}`}
                  >
                    <tab.icon size={14} className="mr-2" /> {tab.label}
                  </DropdownMenuItem>
                ))}
                {permittedTabs.length > 0 && <div className="my-1 border-t border-border" />}
                <DropdownMenuItem
                  onClick={() => setActiveTab("profile")}
                  className={`cursor-pointer ${activeTab === "profile" ? "text-[#007AFF]" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="nav-profile"
                >
                  <IdentificationCard size={14} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-[#FF3B30] focus:text-[#FF3B30] focus:bg-[#FF3B30]/10 cursor-pointer" data-testid="logout-btn">
                  <SignOut size={16} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {activeTab === "validate" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 space-y-4">
              <ValidationForm onSubmit={handleValidate} submitting={submitting} />
              <ValidationResult lastResult={lastResult} />
              <TopRoomsToday />
            </div>
            <div className="lg:col-span-2">
              <LiveRecordsTable validations={validations} timezone={timezone} riskLabels={riskLabels} customLevels={customLevels} newIds={newIds} onPlateUpdated={reload} />
            </div>
          </div>
        ) : ActiveComponent ? (
          <ActiveComponent />
        ) : null}
      </main>

      {/* Guest Info + Validation Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) handleCancelValidation(); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-md" data-testid="validation-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <ShieldCheck size={24} weight="bold" className="text-[#007AFF]" />
              CONFIRM VALIDATION
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-4">
              {/* Car & Room info */}
              <div className="flex items-center gap-6 bg-[#0A0A0A] border border-[#262626] rounded-sm p-4">
                <span className="text-sm text-[#737373]">Car: <span className="font-mono-data text-lg font-bold text-white">{confirmDialog.plate}</span></span>
                <span className="text-sm text-[#737373]">Room: <span className="font-mono-data text-lg font-bold text-white">{confirmDialog.room}</span></span>
              </div>

              {/* Guest Info */}
              {confirmDialog.guest_info?.length > 0 ? (
                <div className="bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-sm p-4 space-y-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#007AFF] font-semibold">Guest Information</p>
                  {confirmDialog.guest_info.map((g, i) => (
                    <div key={i} className="flex flex-col gap-1">
                      {/* VIP + RATE_CODE tags */}
                      <div className="flex items-center gap-2">
                        {g.vip && (
                          <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/40">{g.vip}</span>
                        )}
                        {!g.vip && g.is_long_stay && (
                          <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider bg-[#BF5AF2]/20 text-[#BF5AF2] border border-[#BF5AF2]/40">{g.rate_code}</span>
                        )}
                        {!g.vip && g.rate_code && !g.is_long_stay && (
                          <span className="px-2 py-0.5 rounded-sm text-[10px] font-mono-data tracking-wider text-[#737373]">{g.rate_code}</span>
                        )}
                      </div>
                      <p className="text-base font-semibold text-white">{g.guest_name}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[#32D74B]">Check-in: <span className="font-mono-data">{g.arrival}</span></span>
                        <span className="text-[#FF9F0A]">Check-out: <span className="font-mono-data">{g.departure}</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3">
                  <p className="text-sm text-[#FF9F0A]">No guest registered for this room.</p>
                </div>
              )}

              {/* Over-limit warning */}
              {confirmDialog.over_limit && (
                <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-4 space-y-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-[#FF3B30] font-semibold flex items-center gap-1">
                    <Warning size={14} weight="bold" /> Room Limit Exceeded
                  </p>
                  <p className="text-sm text-[#A3A3A3]">{confirmDialog.over_limit_alert}</p>
                  <p className="text-xs text-[#737373]">
                    Plates for this room today: <span className="font-mono-data text-white">{confirmDialog.plate_count}/{confirmDialog.max_plates}</span>
                  </p>
                  <p className="text-xs text-[#FF9F0A]">This validation will be flagged and the status elevated.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCancelValidation}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm" data-testid="cancel-validation-btn">
              Cancel
            </Button>
            <Button onClick={handleProceed} disabled={submitting}
              className={`rounded-sm text-white ${confirmDialog?.over_limit ? "bg-[#FF3B30] hover:bg-[#FF3B30]/80" : "bg-[#007AFF] hover:bg-[#3395FF]"}`}
              data-testid="proceed-validation-btn">
              {submitting ? "Validating..." : "Proceed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
